/* eslint-disable */
// Admin-only edit dialog for an already-sent dep invitation (iter-116).
// Extracted from EventDetail.jsx in iter-124 (slice 2) to shrink the parent
// page below 4100 LOC.
//
// Props
// -----
//   open              boolean — dialog open state
//   invitation        the current invitation object (or null when closed)
//   deps              full list of band deps (so admin can swap)
//   sections          full list of band sections
//   memberAttendance  list of {player_id, player_name, status} for the
//                     "Replacing which player" dropdown
//   dep_invitations   list of existing invitations on this event (used to
//                     hide deps that already have an invite for this event)
//   editInvite        the form state object owned by the parent
//   setEditInvite     setter for the form state
//   saving            boolean — disables the buttons while saving
//   onClose           call to close the dialog
//   onSave            call to PUT the changes
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

export const EditDepInviteDialog = ({
  editInvite,
  setEditInvite,
  deps,
  sections,
  memberAttendance,
  dep_invitations,
  onClose,
  onSave,
}) => {
  return (
    <Dialog
      open={editInvite.open}
      onOpenChange={(o) =>
        setEditInvite((s) => ({ ...s, open: o, invitation: o ? s.invitation : null }))
      }
    >
      <DialogContent className="sm:max-w-md" data-testid="edit-dep-invite-dialog">
        <DialogHeader>
          <DialogTitle>Edit dep invitation</DialogTitle>
        </DialogHeader>
        {editInvite.invitation && (() => {
          const inv = editInvite.invitation;
          // All band deps (including the current one) so admin can swap.
          const swapDepOptions = deps.filter(
            (d) =>
              d.id === inv.dep_player_id ||
              !dep_invitations.some((i) => i.dep_player_id === d.id)
          );
          const selectedDep = deps.find((d) => d.id === editInvite.dep_player_id);
          // Sections the selected dep is in. Fall back to the band's full
          // list if the dep doesn't have any explicit assignments.
          const depSectionIds = new Set(selectedDep?.section_ids || []);
          const sectionOptions = depSectionIds.size
            ? sections.filter((s) => depSectionIds.has(s.id))
            : sections;
          const replacementOptions = memberAttendance
            .slice()
            .sort((a, b) => (a.player_name || "").localeCompare(b.player_name || ""));
          return (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Dep</Label>
                <Select
                  value={editInvite.dep_player_id}
                  onValueChange={(v) =>
                    setEditInvite((s) => ({
                      ...s,
                      dep_player_id: v,
                      section_id: v === inv.dep_player_id ? s.section_id : "",
                    }))
                  }
                >
                  <SelectTrigger data-testid="edit-dep-invite-dep-select">
                    <SelectValue placeholder="Pick a dep…" />
                  </SelectTrigger>
                  <SelectContent>
                    {swapDepOptions
                      .slice()
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                      .map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                          {d.id === inv.dep_player_id ? "  (current)" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {editInvite.dep_player_id &&
                  editInvite.dep_player_id !== inv.dep_player_id && (
                    <p className="text-xs text-amber-400">
                      Changing the dep will send a fresh invitation email to the new person.
                    </p>
                  )}
              </div>

              <div className="space-y-2">
                <Label>Section</Label>
                <Select
                  value={editInvite.section_id}
                  onValueChange={(v) => setEditInvite((s) => ({ ...s, section_id: v }))}
                >
                  <SelectTrigger data-testid="edit-dep-invite-section-select">
                    <SelectValue placeholder="Pick a section…" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectionOptions.map((sec) => (
                      <SelectItem key={sec.id} value={sec.id}>
                        {sec.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Replacing which player</Label>
                <Select
                  value={editInvite.for_player_id || "__inviter"}
                  onValueChange={(v) =>
                    setEditInvite((s) => ({
                      ...s,
                      for_player_id: v === "__inviter" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger data-testid="edit-dep-invite-replacing-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__inviter">
                      {inv.invited_by_name ? `Inviter (${inv.invited_by_name})` : "Inviter"}
                    </SelectItem>
                    {replacementOptions.map((m) => (
                      <SelectItem key={m.player_id} value={m.player_id}>
                        {m.player_name}
                        {m.status === "not_attending"
                          ? " (Can't go)"
                          : m.status === "maybe"
                            ? " (Maybe)"
                            : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  value={editInvite.message}
                  onChange={(e) =>
                    setEditInvite((s) => ({ ...s, message: e.target.value }))
                  }
                  placeholder="Optional note to the dep…"
                  rows={3}
                  data-testid="edit-dep-invite-message"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={editInvite.saving}
                  data-testid="edit-dep-invite-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={onSave}
                  disabled={editInvite.saving}
                  data-testid="edit-dep-invite-save"
                >
                  {editInvite.saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
};
